import { useMemo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export function WorkspacesPopover({
  data = [],
  value,
  onChange,
  disabled,
  sx,
  tooltip = 'تغییر فضای کاری',
  popoverTitle = 'انتخاب فضای کاری',
  popoverDescription = 'فضای کاری موردنظر خود را انتخاب کنید.',
  secondaryFallback = 'فضای کاری',
  ...other
}) {
  const popover = usePopover();
  const [selectedWorkspace, setSelectedWorkspace] = useState(data[0]);

  const workspace =
    value === undefined ? selectedWorkspace : data.find((option) => option.id === value) || data[0];

  const groups = useMemo(() => {
    const globalOptions = data.filter((option) => option.mode === 'Global');
    const organizationOptions = data.filter((option) => option.mode === 'Organization');
    const otherOptions = data.filter((option) => !option.mode);

    return [
      { key: 'global', title: 'مدیریت سامانه', options: globalOptions },
      { key: 'organization', title: 'سازمان‌ها', options: organizationOptions },
      { key: 'workspace', title: 'فضاهای کاری', options: otherOptions },
    ].filter((group) => group.options.length);
  }, [data]);

  const handleChangeWorkspace = useCallback(
    async (newValue) => {
      if (newValue.id === workspace?.id) {
        popover.onClose();
        return;
      }

      if (onChange) {
        const shouldClose = await onChange(newValue);
        if (shouldClose !== false) popover.onClose();
        return;
      }

      setSelectedWorkspace(newValue);
      popover.onClose();
    },
    [onChange, popover, workspace?.id]
  );

  const secondaryLabel =
    workspace?.description ?? workspace?.role ?? workspace?.plan ?? secondaryFallback;

  return (
    <>
      <Tooltip title={tooltip}>
        <ButtonBase
          disableRipple
          disabled={disabled}
          onClick={popover.onOpen}
          sx={{
            py: 0.5,
            px: { xs: 0.5, sm: 0.75 },
            gap: { xs: 0.5, sm: 1 },
            maxWidth: { xs: 210, sm: 360 },
            borderRadius: 1.5,
            textAlign: 'right',
            '&:hover': { bgcolor: 'action.hover' },
            ...sx,
          }}
          {...other}
        >
          <WorkspaceAvatar workspace={workspace} active />

          <Box
            component="span"
            sx={{
              minWidth: 0,
              flexGrow: 1,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Typography component="span" variant="subtitle2" noWrap sx={{ display: 'block' }}>
              {workspace?.name ?? 'انتخاب فضای کاری'}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="primary.main"
              noWrap
              sx={{ display: 'block' }}
            >
              {secondaryLabel}
            </Typography>
          </Box>

          {disabled ? (
            <CircularProgress size={16} />
          ) : (
            <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
          )}
        </ButtonBase>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-right' } }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
          <Typography variant="subtitle1">{popoverTitle}</Typography>
          <Typography variant="caption" color="text.secondary">
            {popoverDescription}
          </Typography>
        </Box>

        <Divider />

        <MenuList sx={{ width: 320, p: 1 }}>
          {groups.map((group) => (
            <Box component="li" key={group.key} sx={{ listStyle: 'none' }}>
              <ListSubheader
                disableSticky
                component="div"
                sx={{
                  px: 1.25,
                  py: 0.75,
                  bgcolor: 'transparent',
                  color: 'text.disabled',
                  typography: 'overline',
                }}
              >
                {group.title}
              </ListSubheader>

              {group.options.map((option) => {
                const selected = option.id === workspace?.id;
                const optionDescription =
                  option.description ?? option.role ?? option.plan ?? secondaryFallback;

                return (
                  <MenuItem
                    key={option.id}
                    selected={selected}
                    disabled={disabled}
                    onClick={() => handleChangeWorkspace(option)}
                    sx={{ minHeight: 64, px: 1.25, py: 1, mb: 0.5, borderRadius: 1.5 }}
                  >
                    <WorkspaceAvatar workspace={option} active={selected} />

                    <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {option.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={selected ? 'primary.main' : 'text.secondary'}
                        noWrap
                      >
                        {optionDescription}
                      </Typography>
                    </Stack>

                    {selected && (
                      <Iconify
                        width={20}
                        icon="solar:check-circle-bold"
                        sx={{ color: 'primary.main', flexShrink: 0 }}
                      />
                    )}
                  </MenuItem>
                );
              })}
            </Box>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function WorkspaceAvatar({ workspace, active }) {
  return (
    <Avatar
      variant="rounded"
      alt={workspace?.name}
      src={workspace?.logo}
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        bgcolor: active ? 'primary.lighter' : 'background.neutral',
        color: active ? 'primary.dark' : 'text.secondary',
      }}
    >
      {workspace?.icon ? (
        <Iconify icon={workspace.icon} width={20} />
      ) : (
        workspace?.name?.charAt(0)?.toUpperCase()
      )}
    </Avatar>
  );
}
